import React, { useCallback, useEffect, useMemo } from 'react';
import {
  getFeatureFlagByNameSelector,
  isSkuSelectionUndefinedSelector,
  storeIdentifierNameSelector,
} from 'store/selectors';
import { useAppSelector } from 'store/store';
import { favoritesListSelector } from 'store/selectors/userFavoritesSelector';
import { getUserToken } from 'utility/auth-utils';
import useFavorites from 'hooks/useFavorites';
import FavoritesButton from 'common/Favorites/FavoritesButton';
import useList from 'hooks/useLists';
import {
  skuProductsAddedToListsSelector,
  ecodeProductsAddedToListsSelector,
} from 'store/selectors/listsPanelSelector';
import useLogger from 'hooks/useLogger';
import { Button } from '@dsg-tech/homefield-react';
import { signOutAddToListEvent } from 'components/Quickview/analytics/analytics-events';
import { QuickviewDetails, SkuDetails } from '../FulfillmentOptions/types';

type AddToFavoritesProps = {
  onClose: (value: string) => boolean;
  selectedSKU: SkuDetails | undefined;
  attributesState: object;
  template: string;
  quickviewDetails: QuickviewDetails;
  updateSignal: () => void;
};

const AddToFavorites = (props: AddToFavoritesProps) => {
  const { onClose, selectedSKU, attributesState, template, quickviewDetails, updateSignal } = props;
  const storeIdentifierName = useAppSelector(storeIdentifierNameSelector);
  const { addFavorite, removeFavorite, loading, productExistenceCheck, fetchFavorites } =
    useFavorites(storeIdentifierName);
  const { fetchEcodeProductsInLists, fetchSkuProductsInLists, isProductInList } =
    useList(storeIdentifierName);
  const favoritesList = useAppSelector(favoritesListSelector);
  const isSkuSelectionUndefined = useAppSelector(isSkuSelectionUndefinedSelector);
  const listPanelEnabled = useAppSelector(getFeatureFlagByNameSelector('listPanelEnabled'));
  const skuProductsAddedToLists = useAppSelector(skuProductsAddedToListsSelector);
  const ecodeProductsAddedToLists = useAppSelector(ecodeProductsAddedToListsSelector);
  const { warn } = useLogger();

  const ecodeSelection = selectedSKU?.parentPartNumber ?? quickviewDetails?.style?.partNumber;
  const skuSelection = quickviewDetails.skus.length > 1 ? selectedSKU?.partNumber : null;

  useEffect(() => {
    if (listPanelEnabled) {
      fetchSkuProductsInLists(ecodeSelection);
      fetchEcodeProductsInLists();
    } else {
      fetchFavorites();
    }
  }, []);

  const exists = useMemo(
    () =>
      !listPanelEnabled
        ? productExistenceCheck(ecodeSelection, skuSelection, favoritesList)
        : isProductInList(
            ecodeSelection,
            ecodeProductsAddedToLists,
            skuSelection,
            skuProductsAddedToLists,
          ),
    [
      selectedSKU,
      quickviewDetails,
      favoritesList,
      skuProductsAddedToLists,
      ecodeProductsAddedToLists,
    ],
  );

  const isAdded = useMemo(() => {
    return exists ? 'filled' : 'outlined';
  }, [exists]);

  const closeFavoritesPanel = () => {
    if (template === 'full') {
      onClose('addtofavorites');
    }
  };

  const templateHandler = useCallback(() => {
    if (exists) {
      removeFavorite(ecodeSelection, skuSelection ?? '', updateSignal);
      closeFavoritesPanel();
      return;
    }

    addFavorite(ecodeSelection, skuSelection ?? '', updateSignal);
    closeFavoritesPanel();
  }, [exists, selectedSKU, quickviewDetails, updateSignal, attributesState]);

  const handleOpenListPanel = () => {
    window.headerFunctions.openListPanel(
      'add-product',
      {
        ecode: ecodeSelection,
        sku: skuSelection,
      },
      false,
      () => {},
      'PLP',
    );

    onClose('addtofavorites');
  };

  const choosePanelHandler = () => (listPanelEnabled ? handleOpenListPanel() : templateHandler());

  const handleAddToFavorites = useCallback(async () => {
    let userToken;
    try {
      userToken = await getUserToken();
    } catch (e) {
      userToken = undefined;
      warn('AddToFavorites', { ErrorMessage: 'Could not retrieve userToken', Error: e });
    }

    if (!userToken || typeof userToken !== 'string') {
      signOutAddToListEvent();

      if (window?.authFunctions) {
        window.authFunctions.login();
      }
      return;
    }
    choosePanelHandler();
  }, [templateHandler, selectedSKU]);

  const labels = {
    listPanel: {
      add: 'Add to a List',
      remove: 'Manage Saved Item',
    },
    favorites: {
      add: 'Add to favorites',
      remove: 'Remove from favorites',
    },
  };

  const keyValue = listPanelEnabled ? 'listPanel' : 'favorites';

  const buttonCaption = useMemo(
    () => (exists ? labels[keyValue].remove : labels[keyValue].add),
    [exists],
  );

  return (
    <>
      {template === 'full' && (
        <div className="hmf-display-flex hmf-flex-column" data-testid="quickViewModal_actionButton">
          <Button
            variant="primary"
            size="large"
            text={buttonCaption}
            onClick={handleAddToFavorites}
            disabled={isSkuSelectionUndefined || loading}
          />
        </div>
      )}
      {template === 'small' && (
        <FavoritesButton
          icon={isAdded}
          handleFavoritesState={handleAddToFavorites}
          disabled={loading}
        />
      )}
    </>
  );
};

export default AddToFavorites;
